import { Cog8ToothIcon } from "@heroicons/react/24/outline"
import { ToggleField } from "components/styledFormElements/ToggleField"
import InputField from "components/styledFormElements/InputField"
import ColorPickerField from "components/styledFormElements/ColorPickerField"
import FileInputUploadFieldNew from 'components/styledFormElements/FineInputUploadNew'
import { SelectInput } from "components/styledFormElements/SelectInput"
import { useAuth } from "context/authContext"
import { AppLogoSettings, SearchPageConfig, SharedTemplateConfig, UserHeaderConfig } from 'components/settings'
import { Button } from "components/ui/button"
import { useEffect } from "react"
import { useUploadFiles } from "hooks/upload"
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form"
import { useAddSettings, useSettingsData, useUpdateSettings } from "hooks/settings"
import { useNotificationBar } from "context/notificationContext"
import { usePopup } from "context/popupContext"
import { useObjectUrl } from "hooks/useObjectUrl"
import { getImageSrc, getRelativeIconPath, normalizeIconPath } from "services/util"
import OnboardingLandingPageSettings from "components/settings/OnboardingLandingPageSettigs"
import VersionControllerSettings from "components/settings/VersionControllerSettings"

export interface Response {
    records: any[],
    pages?: number,
    total?: number,
    pagination?: Object
}

interface UserHeaderConfigForm {
    image: File | string | null
    heading: string
    description: string
}

interface VoiceAssistantHeaderConfigForm extends UserHeaderConfigForm {
    cta_label: string
}

interface SearchPageSectionConfigForm {
    title: string
    icon: string
}

interface LandingPageConfigForm {
    image: File | string | null
    heading: string
    description?: string
    cta_label?: string
}

interface OnboardingQuestionScreenForm {
    image: File | string | null
    heading: string
    description: string
    cta_text: string
    enable_skip: boolean
    skip_text: string
}

interface SearchPageConfigForm {
    search_home_page_title: string
    tool_section: SearchPageSectionConfigForm
    capsule_section: SearchPageSectionConfigForm
}

interface ShareTemplateConfigForm {
    app_share_template: string
    episode_share_template: string
    pdf_share_template: string
}

interface LogSettingsForm {
    player_logger: boolean
    video_error_logs: boolean
    buffer_logs: boolean
}

interface VersionControlPlatformForm {
    minimum_required_version: string
    force_update_enabled: boolean
    title: string
}

interface VersionControlForm {
    android: VersionControlPlatformForm
    ios: VersionControlPlatformForm
}

interface TruecallerOAuthConfigForm {
    enabled: boolean
    cta_text: string
    footer_type: string
    button_color: string
    button_text_color: string
    consent_heading_option: string
    button_shape: string
    theme: string
    locale: string
    scopes: string[]
}

interface GeneralSettingsForm {
    app_logo_image: File | string | null
    default_avatar: File | string | null
    inclusion_rule: number | ''
    banner_hex_code: string
    enable_onboarding: boolean
    isMultipleSelectFirst: boolean
    isMultipleSelectSecond: boolean
    enableSkipFirst: boolean
    enableSkipSecond: boolean
    skipTextFirst: string
    skipTextSecond: string
    video_id: string
    onboarding_question_screen: OnboardingQuestionScreenForm
    landing_page_one: LandingPageConfigForm
    landing_page_two: LandingPageConfigForm
    first_time_user_header: UserHeaderConfigForm
    second_time_user_header: UserHeaderConfigForm
    discovery_search_header: UserHeaderConfigForm
    voice_assistant_header: VoiceAssistantHeaderConfigForm
    search_page_config: SearchPageConfigForm
    share: ShareTemplateConfigForm
    log_settings: LogSettingsForm
    version_control: VersionControlForm
    truecaller_oauth_config: TruecallerOAuthConfigForm
}

const TRUECALLER_CTA_TEXT_OPTIONS = [
    { value: 'CTA_TEXT_CONTINUE_WITH', display: 'Continue With' },
    { value: 'CTA_TEXT_PROCEED', display: 'Proceed' },
    { value: 'CTA_TEXT_ACCEPT', display: 'Accept' },
    { value: 'CTA_TEXT_CONFIRM', display: 'Confirm' },
    { value: 'CTA_TEXT_VERIFY', display: 'Verify' },
]

const TRUECALLER_FOOTER_TYPE_OPTIONS = [
    { value: 'FOOTER_TYPE_MANUALLY', display: 'Manually' },
    { value: 'FOOTER_TYPE_ANOTHER_MOBILE_NO', display: 'Another Mobile Number' },
    { value: 'FOOTER_TYPE_SKIP', display: 'Skip' },
]

const TRUECALLER_CONSENT_HEADING_OPTIONS = [
    { value: 'SDK_CONSENT_HEADING_LOG_IN_TO', display: 'Log In To' },
    { value: 'SDK_CONSENT_HEADING_SIGN_UP_WITH', display: 'Sign Up With' },
    { value: 'SDK_CONSENT_HEADING_SIGN_IN_TO', display: 'Sign In To' },
    { value: 'SDK_CONSENT_HEADING_VERIFY_NUMBER_WITH', display: 'Verify Number With' },
]

const TRUECALLER_BUTTON_SHAPE_OPTIONS = [
    { value: 'BUTTON_SHAPE_ROUNDED', display: 'Rounded' },
    { value: 'BUTTON_SHAPE_RECTANGLE', display: 'Rectangle' },
]

const TRUECALLER_THEME_OPTIONS = [
    { value: 'THEME_LIGHT', display: 'Light' },
    { value: 'THEME_DARK', display: 'Dark' },
]

const DEFAULT_TRUECALLER_OAUTH_CONFIG: TruecallerOAuthConfigForm = {
    enabled: true,
    cta_text: 'CTA_TEXT_CONTINUE_WITH',
    footer_type: 'FOOTER_TYPE_MANUALLY',
    button_color: '#1A56DB',
    button_text_color: '#FFFFFF',
    consent_heading_option: 'SDK_CONSENT_HEADING_LOG_IN_TO',
    button_shape: 'BUTTON_SHAPE_ROUNDED',
    theme: 'THEME_LIGHT',
    locale: 'en',
    scopes: ['profile', 'phone', 'email', 'openid', 'address'],
}

export const GeneralSettings = () => {
    const notification = useNotificationBar()
    const popup = usePopup()

    const { mutate: addSettings, isLoading: addLoading } = useAddSettings()
    const { mutate: updateSettings, isLoading: updateLoading } = useUpdateSettings()
    const { data: settingsData, refetch } = useSettingsData()
    const uploadMutation = useUploadFiles()
    const auth = useAuth()

    /* ---------------- Theme Switch ---------------- */
    const handlethemeSwitch = (event: any) => {
        if (event?.target?.value && event?.target?.value === 'true') {
            return auth?.setDarkTheme(true)
        }
        auth?.setDarkTheme(false)
    }

    /* ---------------- Upload Helper ---------------- */
    const uploadImage = async (file: File) => {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('customPath', 'settings/images')

        const res = await uploadMutation.mutateAsync({
            data: formData,
        })

        return res?.data?.data?.key || ''
    }

    /* ---------------- General Settings Form ---------------- */
    const generalSetting = settingsData?.data?.find(
        (item: any) => item.slug === 'general_settings'
    )

    const methods = useForm<GeneralSettingsForm>({
        mode: "onChange",
        defaultValues: {
            app_logo_image: null,
            default_avatar: null,
            inclusion_rule: '',
            banner_hex_code: '',
            first_time_user_header: {
                image: null,
                heading: '',
                description: ''
            },
            second_time_user_header: {
                image: null,
                heading: '',
                description: ''
            },
            discovery_search_header: {
                image: null,
                heading: '',
                description: ''
            },
            voice_assistant_header: {
                image: null,
                heading: '',
                description: '',
                cta_label: ''
            },
            enable_onboarding: false,
            isMultipleSelectFirst: false,
            isMultipleSelectSecond: false,
            enableSkipFirst: false,
            enableSkipSecond: false,
            skipTextFirst: '',
            skipTextSecond: '',
            video_id: '',
            onboarding_question_screen: {
                image: null,
                heading: '',
                description: '',
                cta_text: '',
                enable_skip: false,
                skip_text: ''
            },
            landing_page_one: {
                image: null,
                heading: '',
                description: '',
                cta_label: ''
            },
            landing_page_two: {
                image: null,
                heading: '',
                description: '',
                cta_label: ''
            },
            search_page_config: {
                search_home_page_title: '',
                tool_section: {
                    title: '',
                    icon: ''
                },
                capsule_section: {
                    title: '',
                    icon: ''
                }
            },
            share: {
                app_share_template: '',
                episode_share_template: '',
                pdf_share_template: '',
            },
            log_settings: {
                player_logger: false,
                video_error_logs: false,
                buffer_logs: false,
            },
            version_control: {
                android: {
                    minimum_required_version: '',
                    force_update_enabled: false,
                    title: '',
                },
                ios: {
                    minimum_required_version: '',
                    force_update_enabled: false,
                    title: '',
                },
            },
            truecaller_oauth_config: DEFAULT_TRUECALLER_OAUTH_CONFIG,
        }
    })

    const {
        handleSubmit,
        setValue,
        register,
        control,
        formState: { errors, isDirty },
        setError
    } = methods

    const watchedAvatar = useWatch({ control, name: 'default_avatar' })
    const avatarPreview = useObjectUrl(watchedAvatar)
    const watchedQuestionScreenImage = useWatch({ control, name: 'onboarding_question_screen.image' })
    const questionScreenImagePreview = useObjectUrl(watchedQuestionScreenImage)
    const questionScreenSkipEnabled = useWatch({ control, name: 'onboarding_question_screen.enable_skip' })
    const firstPageSkipEnabled = useWatch({ control, name: 'enableSkipFirst' })
    const secondPageSkipEnabled = useWatch({ control, name: 'enableSkipSecond' })

    useEffect(() => {
        if (generalSetting?.settings) {
            setValue(
                'app_logo_image',
                generalSetting.settings.app_logo_image || null,
                { shouldDirty: false }
            )
            setValue(
                'default_avatar',
                generalSetting.settings.default_avatar || null,
                { shouldDirty: false }
            )
            setValue(
                'inclusion_rule',
                generalSetting.settings.inclusion_rule || '',
                { shouldDirty: false }
            )
            setValue(
                'banner_hex_code',
                generalSetting.settings.banner_hex_code || '',
                { shouldDirty: false }
            )

            // Load first_time_user_header settings
            const firstTimeUserSettings = generalSetting.settings?.onboarding?.first_time_user_header || generalSetting.settings.first_time_user_header
            if (firstTimeUserSettings) {
                setValue(
                    'first_time_user_header',
                    {
                        image: firstTimeUserSettings.image || null,
                        heading: firstTimeUserSettings.heading || '',
                        description: firstTimeUserSettings.description || ''
                    },
                    { shouldDirty: false }
                )
            }

            // Load second_time_user_header settings
            const secondTimeUserSettings = generalSetting.settings?.onboarding?.second_time_user_header || generalSetting.settings.second_time_user_header
            if (secondTimeUserSettings) {
                setValue(
                    'second_time_user_header',
                    {
                        image: secondTimeUserSettings.image || null,
                        heading: secondTimeUserSettings.heading || '',
                        description: secondTimeUserSettings.description || ''
                    },
                    { shouldDirty: false }
                )
            }

            // Load discovery_search_header settings
            if (generalSetting.settings.discovery_search_header) {
                setValue(
                    'discovery_search_header',
                    {
                        image: generalSetting.settings.discovery_search_header.image || null,
                        heading: generalSetting.settings.discovery_search_header.heading || '',
                        description: generalSetting.settings.discovery_search_header.description || ''
                    },
                    { shouldDirty: false }
                )
            }

            // Load voice_assistant_header settings
            if (generalSetting.settings.voice_assistant_header) {
                setValue(
                    'voice_assistant_header',
                    {
                        image: generalSetting.settings.voice_assistant_header.image || null,
                        heading: generalSetting.settings.voice_assistant_header.heading || '',
                        description: generalSetting.settings.voice_assistant_header.description || '',
                        cta_label: generalSetting.settings.voice_assistant_header.cta_label || ''
                    },
                    { shouldDirty: false }
                )
            }

            const landingPageOneSettings = generalSetting.settings?.onboarding?.landing_page_one
            if (landingPageOneSettings) {
                setValue(
                    'landing_page_one',
                    {
                        image: landingPageOneSettings.image || null,
                        heading: landingPageOneSettings.heading || '',
                        description: landingPageOneSettings.description || '',
                        cta_label: landingPageOneSettings.cta_label || ''
                    },
                    { shouldDirty: false }
                )
            }

            const questionScreenSettings = generalSetting.settings?.onboarding?.onboarding_question_screen
            if (questionScreenSettings) {
                setValue(
                    'onboarding_question_screen',
                    {
                        image: questionScreenSettings.image || null,
                        heading: questionScreenSettings.heading || '',
                        description: questionScreenSettings.description || '',
                        cta_text: questionScreenSettings.cta_text || '',
                        enable_skip: Boolean(questionScreenSettings.enable_skip),
                        skip_text: questionScreenSettings.skip_text || ''
                    },
                    { shouldDirty: false }
                )
            }

            const landingPageTwoSettings = generalSetting.settings?.onboarding?.landing_page_two
            if (landingPageTwoSettings) {
                setValue(
                    'landing_page_two',
                    {
                        image: landingPageTwoSettings.image || null,
                        heading: landingPageTwoSettings.heading || '',
                        description: landingPageTwoSettings.description || '',
                        cta_label: landingPageTwoSettings.cta_label || ''
                    },
                    { shouldDirty: false }
                )
            }
            setValue(
                'enable_onboarding',
                Boolean(
                    generalSetting.settings?.onboarding?.enable_onboarding ??
                    generalSetting.settings?.onboarding?.enable_onbloarding ??
                    false
                ),
                { shouldDirty: false }
            )
            setValue(
                'isMultipleSelectFirst',
                Boolean(
                    generalSetting.settings?.onboarding?.isMultipleSelectFirst ??
                    generalSetting.settings?.isMultipleSelectFirst ??
                    false
                ),
                { shouldDirty: false }
            )
            setValue(
                'isMultipleSelectSecond',
                Boolean(
                    generalSetting.settings?.onboarding?.isMultipleSelectSecond ??
                    generalSetting.settings?.isMultipleSelectSecond ??
                    false
                ),
                { shouldDirty: false }
            )
            setValue(
                'enableSkipFirst',
                Boolean(generalSetting.settings?.onboarding?.first_time_user_header?.enable_skip ?? false),
                { shouldDirty: false }
            )
            setValue(
                'enableSkipSecond',
                Boolean(generalSetting.settings?.onboarding?.second_time_user_header?.enable_skip ?? false),
                { shouldDirty: false }
            )
            setValue(
                'skipTextFirst',
                generalSetting.settings?.onboarding?.first_time_user_header?.skip_text || '',
                { shouldDirty: false }
            )
            setValue(
                'skipTextSecond',
                generalSetting.settings?.onboarding?.second_time_user_header?.skip_text || '',
                { shouldDirty: false }
            )
            setValue(
                'video_id',
                String(generalSetting.settings?.onboarding?.video_id ?? ''),
                { shouldDirty: false }
            )

            if (generalSetting.settings.search_page_config) {
                const searchPageConfig = generalSetting.settings.search_page_config
                setValue(
                    'search_page_config',
                    {
                        search_home_page_title: searchPageConfig.search_home_page_title || '',
                        tool_section: {
                            title: searchPageConfig.tool_section?.title || '',
                            icon: getImageSrc(searchPageConfig.tool_section?.icon) || ''
                        },
                        capsule_section: {
                            title: searchPageConfig.capsule_section?.title || '',
                            icon: getImageSrc(searchPageConfig.capsule_section?.icon) || ''
                        }
                    },
                    { shouldDirty: false }
                )
            }

            if (generalSetting.settings.share) {
                const shareSettings = generalSetting.settings.share
                setValue(
                    'share',
                    {
                        app_share_template: shareSettings.app_share_template || '',
                        episode_share_template: shareSettings.episode_share_template || '',
                        pdf_share_template: shareSettings.pdf_share_template || '',
                    },
                    { shouldDirty: false }
                )
            }

            if (generalSetting.settings.log_settings) {
                const logSettings = generalSetting.settings.log_settings
                setValue(
                    'log_settings',
                    {
                        player_logger: Boolean(logSettings.player_logger),
                        video_error_logs: Boolean(logSettings.video_error_logs),
                        buffer_logs: Boolean(logSettings.buffer_logs),
                    },
                    { shouldDirty: false }
                )
            }

            if (generalSetting.settings.version_control) {
                const versionControl = generalSetting.settings.version_control
                setValue(
                    'version_control',
                    {
                        android: {
                            minimum_required_version:
                                versionControl.android?.minimum_required_version || '',
                            force_update_enabled: Boolean(
                                versionControl.android?.force_update_enabled
                            ),
                            title: versionControl.android?.title || '',
                        },
                        ios: {
                            minimum_required_version:
                                versionControl.ios?.minimum_required_version || '',
                            force_update_enabled: Boolean(
                                versionControl.ios?.force_update_enabled
                            ),
                            title: versionControl.ios?.title || '',
                        },
                    },
                    { shouldDirty: false }
                )
            }

            const truecallerOAuthConfig = generalSetting.settings.truecaller_oauth_config
            setValue(
                'truecaller_oauth_config',
                {
                    ...DEFAULT_TRUECALLER_OAUTH_CONFIG,
                    ...(truecallerOAuthConfig || {}),
                    enabled: Boolean(
                        truecallerOAuthConfig?.enabled ??
                        DEFAULT_TRUECALLER_OAUTH_CONFIG.enabled
                    ),
                    scopes: DEFAULT_TRUECALLER_OAUTH_CONFIG.scopes
                },
                { shouldDirty: false }
            )
        }
    }, [generalSetting, setValue])

    const onSubmit = (data: GeneralSettingsForm) => {
        // Guard: no changes, no submit
        if (!isDirty) {
            notification.push({
                title: 'Info',
                type: 'info',
                message: 'No changes to save',
            })
            return
        }

        popup.open({
            title: 'Confirm Save',
            message: 'Save General Settings?',
            type: 'confirm',
            confirmButtonText: 'Save',
            cancelButtonText: 'Cancel',
            onAction: async (confirmed: boolean) => {
                if (!confirmed) return

                let logoImageKey = data.app_logo_image
                let avatarKey = data.default_avatar
                let firstTimeUserImageKey = data.first_time_user_header?.image
                let secondTimeUserImageKey = data.second_time_user_header?.image
                let questionScreenImageKey = data.onboarding_question_screen?.image
                let discoverySearchImageKey = data.discovery_search_header?.image
                let voiceAssistantImageKey = data.voice_assistant_header?.image

                // Upload app logo if it's a new file
                if (data.app_logo_image instanceof File) {
                    try {
                        logoImageKey = await uploadImage(data.app_logo_image)
                    } catch {
                        setError('app_logo_image', {
                            type: 'upload',
                            message: 'Failed to upload logo'
                        })
                        return
                    }
                }

                // Upload default avatar if it's a new file
                if (data.default_avatar instanceof File) {
                    try {
                        avatarKey = await uploadImage(data.default_avatar)
                    } catch {
                        setError('default_avatar', {
                            type: 'upload',
                            message: 'Avatar upload failed'
                        })
                        return
                    }
                }

                // Upload first time user header image if it's a new file
                if (data.first_time_user_header?.image instanceof File) {
                    try {
                        firstTimeUserImageKey = await uploadImage(data.first_time_user_header.image)
                    } catch {
                        setError('first_time_user_header.image', {
                            type: 'upload',
                            message: 'Failed to upload first time user header image'
                        })
                        return
                    }
                }

                // Upload second time user header image if it's a new file
                if (data.second_time_user_header?.image instanceof File) {
                    try {
                        secondTimeUserImageKey = await uploadImage(data.second_time_user_header.image)
                    } catch {
                        setError('second_time_user_header.image', {
                            type: 'upload',
                            message: 'Failed to upload second time user header image'
                        })
                        return
                    }
                }

                if (data.onboarding_question_screen?.image instanceof File) {
                    try {
                        questionScreenImageKey = await uploadImage(data.onboarding_question_screen.image)
                    } catch {
                        setError('onboarding_question_screen.image', {
                            type: 'upload',
                            message: 'Failed to upload onboarding question screen image'
                        })
                        return
                    }
                }

                // Upload discovery search header image if it's a new file
                if (data.discovery_search_header?.image instanceof File) {
                    try {
                        discoverySearchImageKey = await uploadImage(data.discovery_search_header.image)
                    } catch {
                        setError('discovery_search_header.image', {
                            type: 'upload',
                            message: 'Failed to upload discovery search header image'
                        })
                        return
                    }
                }

                // Upload voice assistant header image if it's a new file
                if (data.voice_assistant_header?.image instanceof File) {
                    try {
                        voiceAssistantImageKey = await uploadImage(data.voice_assistant_header.image)
                    } catch {
                        setError('voice_assistant_header.image', {
                            type: 'upload',
                            message: 'Failed to upload voice assistant header image'
                        })
                        return
                    }
                }

                let landingPageOneImageKey = data.landing_page_one?.image
                let landingPageTwoImageKey = data.landing_page_two?.image

                if (data.landing_page_one?.image instanceof File) {
                    try {
                        landingPageOneImageKey = await uploadImage(data.landing_page_one.image)
                    } catch {
                        setError('landing_page_one.image', {
                            type: 'upload',
                            message: 'Failed to upload landing page one image'
                        })
                        return
                    }
                }

                if (data.landing_page_two?.image instanceof File) {
                    try {
                        landingPageTwoImageKey = await uploadImage(data.landing_page_two.image)
                    } catch {
                        setError('landing_page_two.image', {
                            type: 'upload',
                            message: 'Failed to upload landing page two image'
                        })
                        return
                    }
                }

                const toolIconPath = data.search_page_config?.tool_section?.icon
                    ? normalizeIconPath(getRelativeIconPath(data.search_page_config?.tool_section?.icon))
                    : null
                const capsuleIconPath = data.search_page_config?.capsule_section?.icon
                    ? normalizeIconPath(getRelativeIconPath(data.search_page_config?.capsule_section?.icon))
                    : null
                const payload = {
                    slug: "general_settings",
                    settings: {
                        app_logo_image: logoImageKey || generalSetting?.settings?.app_logo_image || '',
                        default_avatar: avatarKey || generalSetting?.settings?.default_avatar || '',
                        inclusion_rule: Number(data.inclusion_rule) || generalSetting?.settings?.inclusion_rule || 0,
                        banner_hex_code: data.banner_hex_code || generalSetting?.settings?.banner_hex_code || '',
                        onboarding: {
                            enable_onboarding: data.enable_onboarding,
                            isMultipleSelectFirst: data.isMultipleSelectFirst,
                            isMultipleSelectSecond: data.isMultipleSelectSecond,
                            video_id: data.video_id ?? generalSetting?.settings?.onboarding?.video_id ?? '',
                            onboarding_question_screen: {
                                image: questionScreenImageKey || generalSetting?.settings?.onboarding?.onboarding_question_screen?.image || '',
                                heading: data.onboarding_question_screen?.heading || '',
                                description: data.onboarding_question_screen?.description || '',
                                cta_text: data.onboarding_question_screen?.cta_text || '',
                                enable_skip: data.onboarding_question_screen?.enable_skip,
                                skip_text: data.onboarding_question_screen?.enable_skip
                                    ? data.onboarding_question_screen?.skip_text || ''
                                    : ''
                            },
                            landing_page_one: {
                                image: landingPageOneImageKey || generalSetting?.settings?.onboarding?.landing_page_one?.image || generalSetting?.settings?.landing_page_one?.image || '',
                                heading: data.landing_page_one?.heading || generalSetting?.settings?.onboarding?.landing_page_one?.heading || generalSetting?.settings?.landing_page_one?.heading || '',
                                description: data.landing_page_one?.description || generalSetting?.settings?.onboarding?.landing_page_one?.description || generalSetting?.settings?.landing_page_one?.description || '',
                                cta_label: data.landing_page_one?.cta_label || generalSetting?.settings?.onboarding?.landing_page_one?.cta_label || generalSetting?.settings?.landing_page_one?.cta_label || ''
                            },
                            landing_page_two: {
                                image: landingPageTwoImageKey || generalSetting?.settings?.onboarding?.landing_page_two?.image || generalSetting?.settings?.landing_page_two?.image || '',
                                heading: data.landing_page_two?.heading || generalSetting?.settings?.onboarding?.landing_page_two?.heading || generalSetting?.settings?.landing_page_two?.heading || '',
                                description: data.landing_page_two?.description || generalSetting?.settings?.onboarding?.landing_page_two?.description || generalSetting?.settings?.landing_page_two?.description || '',
                                cta_label: data.landing_page_two?.cta_label || generalSetting?.settings?.onboarding?.landing_page_two?.cta_label || generalSetting?.settings?.landing_page_two?.cta_label || ''
                            },
                            first_time_user_header: {
                                image: firstTimeUserImageKey || generalSetting?.settings?.onboarding?.first_time_user_header?.image || generalSetting?.settings?.first_time_user_header?.image || '',
                                heading: data.first_time_user_header?.heading || generalSetting?.settings?.onboarding?.first_time_user_header?.heading || generalSetting?.settings?.first_time_user_header?.heading || '',
                                description: data.first_time_user_header?.description || generalSetting?.settings?.onboarding?.first_time_user_header?.description || generalSetting?.settings?.first_time_user_header?.description || '',
                                enable_skip: data.enableSkipFirst,
                                skip_text: data.enableSkipFirst ? data.skipTextFirst || '' : ''
                            },
                            second_time_user_header: {
                                image: secondTimeUserImageKey || generalSetting?.settings?.onboarding?.second_time_user_header?.image || generalSetting?.settings?.second_time_user_header?.image || '',
                                heading: data.second_time_user_header?.heading || generalSetting?.settings?.onboarding?.second_time_user_header?.heading || generalSetting?.settings?.second_time_user_header?.heading || '',
                                description: data.second_time_user_header?.description || generalSetting?.settings?.onboarding?.second_time_user_header?.description || generalSetting?.settings?.second_time_user_header?.description || '',
                                enable_skip: data.enableSkipSecond,
                                skip_text: data.enableSkipSecond ? data.skipTextSecond || '' : ''
                            },
                        },
                        first_time_user_header: {
                            image: firstTimeUserImageKey || generalSetting?.settings?.onboarding?.enable?.first_time_user_header?.image || generalSetting?.settings?.first_time_user_header?.image || '',
                            heading: data.first_time_user_header?.heading || generalSetting?.settings?.onboarding?.enable?.first_time_user_header?.heading || generalSetting?.settings?.first_time_user_header?.heading || '',
                            description: data.first_time_user_header?.description || generalSetting?.settings?.onboarding?.enable?.first_time_user_header?.description || generalSetting?.settings?.first_time_user_header?.description || ''
                        },
                        second_time_user_header: {
                            image: secondTimeUserImageKey || generalSetting?.settings?.onboarding?.enable?.second_time_user_header?.image || generalSetting?.settings?.second_time_user_header?.image || '',
                            heading: data.second_time_user_header?.heading || generalSetting?.settings?.onboarding?.enable?.second_time_user_header?.heading || generalSetting?.settings?.second_time_user_header?.heading || '',
                            description: data.second_time_user_header?.description || generalSetting?.settings?.onboarding?.enable?.second_time_user_header?.description || generalSetting?.settings?.second_time_user_header?.description || ''
                        },
                        discovery_search_header: {
                            image: discoverySearchImageKey || generalSetting?.settings?.discovery_search_header?.image || '',
                            heading: data.discovery_search_header?.heading || generalSetting?.settings?.discovery_search_header?.heading || '',
                            description: data.discovery_search_header?.description || generalSetting?.settings?.discovery_search_header?.description || ''
                        },
                        voice_assistant_header: {
                            image: voiceAssistantImageKey || generalSetting?.settings?.voice_assistant_header?.image || '',
                            heading: data.voice_assistant_header?.heading || generalSetting?.settings?.voice_assistant_header?.heading || '',
                            description: data.voice_assistant_header?.description || generalSetting?.settings?.voice_assistant_header?.description || '',
                            cta_label: data.voice_assistant_header?.cta_label || generalSetting?.settings?.voice_assistant_header?.cta_label || ''
                        },
                        search_page_config: {
                            search_home_page_title: data.search_page_config?.search_home_page_title || '',
                            tool_section: {
                                title: data.search_page_config?.tool_section?.title || '',
                                icon: toolIconPath || ''
                            },
                            capsule_section: {
                                title: data.search_page_config?.capsule_section?.title || '',
                                icon: capsuleIconPath || ''
                            }
                        },
                        share: {
                            app_share_template:
                                data.share?.app_share_template ||
                                generalSetting?.settings?.share?.app_share_template ||
                                '',
                            episode_share_template:
                                data.share?.episode_share_template ||
                                generalSetting?.settings?.share?.episode_share_template ||
                                '',
                            pdf_share_template:
                                data.share?.pdf_share_template ||
                                generalSetting?.settings?.share?.pdf_share_template ||
                                '',
                        },
                        log_settings: {
                            player_logger:
                                data.log_settings?.player_logger ??
                                generalSetting?.settings?.log_settings?.player_logger ??
                                false,
                            video_error_logs:
                                data.log_settings?.video_error_logs ??
                                generalSetting?.settings?.log_settings?.video_error_logs ??
                                false,
                            buffer_logs:
                                data.log_settings?.buffer_logs ??
                                generalSetting?.settings?.log_settings?.buffer_logs ??
                                false,
                        },
                        version_control: {
                            android: {
                                minimum_required_version:
                                    data.version_control?.android?.minimum_required_version ||
                                    generalSetting?.settings?.version_control?.android
                                        ?.minimum_required_version ||
                                    '',
                                force_update_enabled:
                                    data.version_control?.android?.force_update_enabled ??
                                    generalSetting?.settings?.version_control?.android
                                        ?.force_update_enabled ??
                                    false,
                                title:
                                    data.version_control?.android?.title ||
                                    generalSetting?.settings?.version_control?.android?.title ||
                                    '',
                            },
                            ios: {
                                minimum_required_version:
                                    data.version_control?.ios?.minimum_required_version ||
                                    generalSetting?.settings?.version_control?.ios
                                        ?.minimum_required_version ||
                                    '',
                                force_update_enabled:
                                    data.version_control?.ios?.force_update_enabled ??
                                    generalSetting?.settings?.version_control?.ios
                                        ?.force_update_enabled ??
                                    false,
                                title:
                                    data.version_control?.ios?.title ||
                                    generalSetting?.settings?.version_control?.ios?.title ||
                                    '',
                            },
                        },
                        truecaller_oauth_config: {
                            enabled:
                                data.truecaller_oauth_config?.enabled ??
                                generalSetting?.settings?.truecaller_oauth_config?.enabled ??
                                DEFAULT_TRUECALLER_OAUTH_CONFIG.enabled,
                            cta_text:
                                data.truecaller_oauth_config?.cta_text ||
                                generalSetting?.settings?.truecaller_oauth_config?.cta_text ||
                                DEFAULT_TRUECALLER_OAUTH_CONFIG.cta_text,
                            footer_type:
                                data.truecaller_oauth_config?.footer_type ||
                                generalSetting?.settings?.truecaller_oauth_config?.footer_type ||
                                DEFAULT_TRUECALLER_OAUTH_CONFIG.footer_type,
                            button_color:
                                data.truecaller_oauth_config?.button_color ||
                                generalSetting?.settings?.truecaller_oauth_config?.button_color ||
                                DEFAULT_TRUECALLER_OAUTH_CONFIG.button_color,
                            button_text_color:
                                data.truecaller_oauth_config?.button_text_color ||
                                generalSetting?.settings?.truecaller_oauth_config?.button_text_color ||
                                DEFAULT_TRUECALLER_OAUTH_CONFIG.button_text_color,
                            consent_heading_option:
                                data.truecaller_oauth_config?.consent_heading_option ||
                                generalSetting?.settings?.truecaller_oauth_config?.consent_heading_option ||
                                DEFAULT_TRUECALLER_OAUTH_CONFIG.consent_heading_option,
                            button_shape:
                                data.truecaller_oauth_config?.button_shape ||
                                generalSetting?.settings?.truecaller_oauth_config?.button_shape ||
                                DEFAULT_TRUECALLER_OAUTH_CONFIG.button_shape,
                            theme:
                                data.truecaller_oauth_config?.theme ||
                                generalSetting?.settings?.truecaller_oauth_config?.theme ||
                                DEFAULT_TRUECALLER_OAUTH_CONFIG.theme,
                            locale:
                                data.truecaller_oauth_config?.locale ||
                                generalSetting?.settings?.truecaller_oauth_config?.locale ||
                                DEFAULT_TRUECALLER_OAUTH_CONFIG.locale,
                            scopes: DEFAULT_TRUECALLER_OAUTH_CONFIG.scopes,
                        },
                    },
                    status: 1,
                    is_delete: 0
                }

                if (generalSetting) {
                    updateSettings(
                        { data: payload, setting_id: generalSetting.id },
                        {
                            onSuccess: () => {
                                notification.push({
                                    title: 'Success',
                                    type: 'success',
                                    message: `General settings saved successfully`,
                                })
                                refetch()
                            },
                            onError: () => {
                                notification.push({
                                    title: 'Error',
                                    type: 'error',
                                    message: `Something went wrong`,
                                })
                            }
                        }
                    )
                } else {
                    addSettings(
                        { data: payload },
                        {
                            onSuccess: () => {
                                notification.push({
                                    title: 'Success',
                                    type: 'success',
                                    message: `General settings saved successfully`,
                                })
                                refetch()
                            },
                            onError: () => {
                                notification.push({
                                    title: 'Error',
                                    type: 'error',
                                    message: `Something went wrong`,
                                })
                            }
                        }
                    )
                }
            }
        })
    }

    return (
        <div className='w-full px-6 pt-6 bg-white dark:text-gray-200 dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-800 shadow-md '>
            <aside className="flex flex-auto items-center gap-2">
                <Cog8ToothIcon className="h-6 w-6" />
                <h4 className="text-xl">General Settings</h4>
            </aside>

            <div className="my-5">
                {/* Headings Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                    <div>
                        <h4 className="font-semibold">Theming</h4>
                    </div>
                    <div>
                        <h4 className="font-semibold">Log Settings</h4>
                    </div>
                </div>

                <hr className="my-2" />

                {/* Content Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                    {/* Theming Content */}
                    <div className="p-4 border border-gray-200 dark:border-slate-700 rounded-lg">
                        <ToggleField
                            value={(!auth?.darkTheme).toString()}
                            checked={auth?.darkTheme ?? false}
                            onChange={handlethemeSwitch}
                            display="Enabled"
                            label="Dark Theme:"
                        />
                        <div className="mt-4">
                            <Controller
                                name="banner_hex_code"
                                control={control}
                                render={({ field }) => (
                                    <ColorPickerField
                                        label="Banner Hex Code"
                                        value={field.value}
                                        onChange={field.onChange}
                                        placeholder="#RRGGBB"
                                    />
                                )}
                            />
                        </div>
                    </div>

                    {/* Log Settings Content */}
                    <div className="p-4 border border-gray-200 dark:border-slate-700 rounded-lg">
                        <div className="space-y-3">
                            <ToggleField
                                register={register('log_settings.player_logger')}
                                display="Enable/Disable"
                                label="Player Logger"
                            />
                            <ToggleField
                                register={register('log_settings.video_error_logs')}
                                display="Enable/Disable"
                                label="Video Error Logs"
                            />
                            <ToggleField
                                register={register('log_settings.buffer_logs')}
                                display="Enable/Disable"
                                label="Buffer Logs"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="my-5">
                        <h4 className="font-semibold">App Logo</h4>
                        <hr className="my-2" />
                        <div className="max-w-[600px]">
                            <AppLogoSettings
                                onValueChange={() => { }}
                            />
                        </div>
                    </div>

                    <div className="my-5">
                        <h4 className="font-semibold">Profile General Settings</h4>
                        <hr className="my-2" />
                        <div className="max-w-[600px]">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Default Avatar */}
                                <FileInputUploadFieldNew
                                    label="Default Avatar"
                                    name="default_avatar"
                                    control={control as any}
                                    errors={errors}
                                    preview={getImageSrc(avatarPreview)}
                                    allowedTypes={[
                                        'image/jpeg',
                                        'image/png',
                                        'image/jpg',
                                        'image/webp',
                                        'image/gif'
                                    ]}
                                    tooltip={{
                                        title: 'Image requirements',
                                        items: [
                                            '2:3 aspect ratio',
                                            'JPG, PNG, JPGE, WEBP, GIF',
                                            '≤ 1 MB',
                                        ],
                                        position: "top"
                                    }}
                                />

                                {/* Inclusion Rule */}
                                <InputField
                                    label="Inclusion Rule (%)"
                                    type="number"
                                    helpText="Minimum percentage of episodes watched"
                                    register={register("inclusion_rule", {
                                        required: "Inclusion rule is Required",
                                        min: {
                                            value: 1,
                                            message: "Must be greater than 0"
                                        },
                                        max: {
                                            value: 99,
                                            message: "Must be less than 100"
                                        },
                                        valueAsNumber: true
                                    })}
                                    error={errors.inclusion_rule?.message as string}
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    {/* User Header Configurations */}
                    <div className="my-5">
                        <h4 className="font-semibold">Onboarding Question Page Settings</h4>
                        <hr className="my-2" />
                        <div className="w-full p-4 border border-gray-200 dark:border-slate-700 rounded-lg mb-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                                <div>
                                    <label className="flex items-center gap-3 cursor-pointer select-none">
                                        <input
                                            type="checkbox"
                                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                            {...register('enable_onboarding')}
                                        />
                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                            Enable Onboarding
                                        </span>
                                    </label>
                                </div>
                                <InputField
                                    label="Video ID"
                                    type="text"
                                    required
                                    helpText="Identifier for the onboarding video"
                                    register={register('video_id', {
                                        required: 'Video ID is required'
                                    })}
                                    error={errors.video_id?.message as string}
                                />
                            </div>
                        </div>
                        <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch mb-6">
                            {/* First Time User Header */}
                            <div className="p-4 border border-gray-200 dark:border-slate-700 rounded-lg h-full flex flex-col">
                                <OnboardingLandingPageSettings
                                    prefix="landing_page_one"
                                    title="Landing Page One"
                                />
                            </div>

                            {/* Second Time User Header */}
                            <div className="p-4 border border-gray-200 dark:border-slate-700 rounded-lg h-full flex flex-col">
                                <OnboardingLandingPageSettings
                                    prefix="landing_page_two"
                                    title="Landing Page Two"
                                />
                            </div>
                        </div>
                        <div className="w-full p-4 border border-gray-200 dark:border-slate-700 rounded-lg mb-6">
                            <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
                                Onboarding Question Screen
                            </h5>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                                <FileInputUploadFieldNew
                                    label="Image"
                                    name="onboarding_question_screen.image"
                                    control={control as any}
                                    errors={errors}
                                    preview={getImageSrc(questionScreenImagePreview)}
                                    allowedTypes={[
                                        'image/jpeg',
                                        'image/png',
                                        'image/jpg',
                                        'image/webp',
                                        'image/gif'
                                    ]}
                                    required
                                    rules={{ required: 'Image is required' }}
                                    tooltip={{
                                        title: 'Image requirements',
                                        items: ['JPG, PNG, JPEG, WEBP, GIF', '≤ 1 MB'],
                                        position: 'top'
                                    }}
                                />
                                <InputField
                                    label="Heading"
                                    type="text"
                                    required
                                    register={register('onboarding_question_screen.heading', {
                                        required: 'Heading is required'
                                    })}
                                    error={errors.onboarding_question_screen?.heading?.message as string}
                                />
                                <InputField
                                    label="Description"
                                    isTextarea
                                    row={3}
                                    required
                                    register={register('onboarding_question_screen.description', {
                                        required: 'Description is required'
                                    })}
                                    error={errors.onboarding_question_screen?.description?.message as string}
                                />
                                <InputField
                                    label="CTA Text"
                                    type="text"
                                    required
                                    register={register('onboarding_question_screen.cta_text', {
                                        required: 'CTA text is required'
                                    })}
                                    error={errors.onboarding_question_screen?.cta_text?.message as string}
                                />
                                <label className="flex items-center gap-3 cursor-pointer select-none md:col-span-2">
                                    <input
                                        id="questionScreenEnableSkip"
                                        type="checkbox"
                                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        {...register('onboarding_question_screen.enable_skip')}
                                    />
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                        Enable Skip
                                    </span>
                                </label>
                                {questionScreenSkipEnabled && (
                                    <div className="md:col-span-2 md:max-w-[calc(50%-0.75rem)]">
                                        <InputField
                                            label="Skip Text"
                                            type="text"
                                            required
                                            register={register('onboarding_question_screen.skip_text', {
                                                required: 'Skip text is required'
                                            })}
                                            error={errors.onboarding_question_screen?.skip_text?.message as string}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
                            {/* First Time User Header */}
                            <div className="p-4 border border-gray-200 dark:border-slate-700 rounded-lg h-full flex flex-col">
                                <UserHeaderConfig
                                    prefix="first_time_user_header"
                                    title="Onboarding Question Page One"
                                />
                                <div className="mt-4 flex items-center gap-3">
                                    <input
                                        id="isMultipleSelectFirst"
                                        type="checkbox"
                                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        {...register('isMultipleSelectFirst')}
                                    />
                                    <label
                                        htmlFor="isMultipleSelectFirst"
                                        className="text-sm font-medium text-gray-700 dark:text-gray-200 cursor-pointer select-none"
                                    >
                                        Allow multiple select
                                    </label>
                                </div>
                                <div className="mt-4 flex items-center gap-3">
                                    <input
                                        id="enableSkipFirst"
                                        type="checkbox"
                                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        {...register('enableSkipFirst')}
                                    />
                                    <label
                                        htmlFor="enableSkipFirst"
                                        className="text-sm font-medium text-gray-700 dark:text-gray-200 cursor-pointer select-none"
                                    >
                                        Enable Skip
                                    </label>
                                </div>
                                {firstPageSkipEnabled && (
                                    <div className="mt-4">
                                        <InputField
                                            label="Skip Text"
                                            type="text"
                                            required
                                            register={register('skipTextFirst', {
                                                required: 'Skip text is required'
                                            })}
                                            error={errors.skipTextFirst?.message as string}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Second Time User Header */}
                            <div className="p-4 border border-gray-200 dark:border-slate-700 rounded-lg h-full flex flex-col">
                                <UserHeaderConfig
                                    prefix="second_time_user_header"
                                    title="Onboarding Question Page Two"
                                />
                                <div className="mt-4 flex items-center gap-3">
                                    <input
                                        id="isMultipleSelectSecond"
                                        type="checkbox"
                                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        {...register('isMultipleSelectSecond')}
                                    />
                                    <label
                                        htmlFor="isMultipleSelectSecond"
                                        className="text-sm font-medium text-gray-700 dark:text-gray-200 cursor-pointer select-none"
                                    >
                                        Allow multiple select
                                    </label>
                                </div>
                                <div className="mt-4 flex items-center gap-3">
                                    <input
                                        id="enableSkipSecond"
                                        type="checkbox"
                                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        {...register('enableSkipSecond')}
                                    />
                                    <label
                                        htmlFor="enableSkipSecond"
                                        className="text-sm font-medium text-gray-700 dark:text-gray-200 cursor-pointer select-none"
                                    >
                                        Enable Skip
                                    </label>
                                </div>
                                {secondPageSkipEnabled && (
                                    <div className="mt-4">
                                        <InputField
                                            label="Skip Text"
                                            type="text"
                                            required
                                            register={register('skipTextSecond', {
                                                required: 'Skip text is required'
                                            })}
                                            error={errors.skipTextSecond?.message as string}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="my-5">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <h4 className="font-semibold">Discovery Search Header</h4>
                            <h4 className="font-semibold">Voice Assistant Header</h4>
                        </div>
                        <hr className="my-2" />
                        <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
                            {/* First Time User Header */}
                            <div className="p-4 border border-gray-200 dark:border-slate-700 rounded-lg h-full flex flex-col">
                                <UserHeaderConfig
                                    prefix="discovery_search_header"
                                />
                            </div>

                            {/* Second Time User Header */}
                            <div className="p-4 border border-gray-200 dark:border-slate-700 rounded-lg h-full flex flex-col">
                                <UserHeaderConfig
                                    prefix="voice_assistant_header"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="my-5">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Search Page Config */}
                            <div className="col-span-1 lg:col-span-2">
                                <h4 className="font-semibold">Search Page Config</h4>
                                <hr className="my-2" />

                                <div className="w-full p-4 border border-gray-200 dark:border-slate-700 rounded-lg">
                                    <SearchPageConfig />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="my-5">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="col-span-1 lg:col-span-2">
                                <h4 className="font-semibold">Shared template Config</h4>
                                <hr className="my-2" />
                                <div className="w-full p-4 border border-gray-200 dark:border-slate-700 rounded-lg">
                                    <SharedTemplateConfig />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="my-5">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="col-span-1 lg:col-span-2">
                                <h4 className="font-semibold">App Version Controller Settings</h4>
                                <hr className="my-2" />
                                    <VersionControllerSettings />
                            </div>
                        </div>
                    </div>

                    <div className="my-5">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="col-span-1 lg:col-span-2">
                                <h4 className="font-semibold">Truecaller Settings</h4>
                                <hr className="my-2" />
                                <div className="w-full p-4 border border-gray-200 dark:border-slate-700 rounded-lg">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <ToggleField
                                            register={register('truecaller_oauth_config.enabled')}
                                            display="Enable/Disable"
                                            label="Enable Truecaller OAuth"
                                        />

                                        <SelectInput
                                            control={control as any}
                                            name="truecaller_oauth_config.cta_text"
                                            label="CTA Text"
                                            options={TRUECALLER_CTA_TEXT_OPTIONS}
                                        />

                                        <SelectInput
                                            control={control as any}
                                            name="truecaller_oauth_config.footer_type"
                                            label="Footer Type"
                                            options={TRUECALLER_FOOTER_TYPE_OPTIONS}
                                        />

                                        <SelectInput
                                            control={control as any}
                                            name="truecaller_oauth_config.consent_heading_option"
                                            label="Consent Heading"
                                            options={TRUECALLER_CONSENT_HEADING_OPTIONS}
                                        />

                                        <SelectInput
                                            control={control as any}
                                            name="truecaller_oauth_config.button_shape"
                                            label="Button Shape"
                                            options={TRUECALLER_BUTTON_SHAPE_OPTIONS}
                                        />

                                        <SelectInput
                                            control={control as any}
                                            name="truecaller_oauth_config.theme"
                                            label="Theme"
                                            options={TRUECALLER_THEME_OPTIONS}
                                        />

                                        <Controller
                                            name="truecaller_oauth_config.button_color"
                                            control={control}
                                            render={({ field }) => (
                                                <ColorPickerField
                                                    label="Button Color"
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    placeholder="#RRGGBB"
                                                />
                                            )}
                                        />

                                        <Controller
                                            name="truecaller_oauth_config.button_text_color"
                                            control={control}
                                            render={({ field }) => (
                                                <ColorPickerField
                                                    label="Button Text Color"
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    placeholder="#RRGGBB"
                                                />
                                            )}
                                        />

                                        <InputField
                                            label="Locale"
                                            type="text"
                                            helpText="Example: en, hi, mr, bn, ta"
                                            register={register('truecaller_oauth_config.locale', {
                                                required: 'Locale is required'
                                            })}
                                            error={errors.truecaller_oauth_config?.locale?.message as string}
                                            required
                                        />

                                        <div className="md:col-span-2 rounded-lg border border-gray-200 dark:border-slate-700 p-3 text-sm text-gray-700 dark:text-gray-200">
                                            <div className="font-semibold mb-1">Default Scopes</div>
                                            <div>profile, phone, email, openid, address</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Single Save Button */}
                    <div className="my-5">
                        <div className="flex justify-left">
                            <Button
                                color="blue"
                                type="submit"
                                disabled={!isDirty}
                                isLoading={updateLoading || addLoading}
                            >
                                Save General Settings
                            </Button>
                        </div>
                    </div>
                </form>
            </FormProvider>
        </div>
    )
}
